import { useEffect, useRef, useState } from 'react';
import { FaPaperclip, FaPaperPlane, FaTimes } from 'react-icons/fa';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const MAX_VIDEO_DURATION = 60; // seconds

/**
 * Message input with text, media upload, typing indicators, and reply functionality.
 * @param {{ onSendMessage: (text: string, replyToId?: number) => void, onUploadMedia: (file: File, caption: string) => void, onTyping: (isTyping: boolean) => void, disabled?: boolean, replyingTo?: object, onCancelReply?: () => void }} props
 */
export function MessageInput({
  onSendMessage,
  onUploadMedia,
  onTyping,
  disabled = false,
  replyingTo = null,
  onCancelReply,
}) {
  const [messageText, setMessageText] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type);
    const isVideo = ACCEPTED_VIDEO_TYPES.includes(file.type);

    if (!isImage && !isVideo) {
      alert('Please select an image or video file.');
      event.target.value = '';
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      alert(`File is too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024} MB.`);
      event.target.value = '';
      return;
    }

    // For videos, validate duration
    if (isVideo) {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);

        if (video.duration > MAX_VIDEO_DURATION) {
          alert(`Video is too long. Maximum duration is ${MAX_VIDEO_DURATION} seconds.`);
          event.target.value = '';
          return;
        }

        // Duration is valid, proceed
        const url = URL.createObjectURL(file);
        setAttachedFile(file);
        setPreviewUrl(url);
      };

      video.onerror = () => {
        // Can't read metadata, accept anyway and let server validate
        URL.revokeObjectURL(video.src);
        const url = URL.createObjectURL(file);
        setAttachedFile(file);
        setPreviewUrl(url);
      };

      video.src = URL.createObjectURL(file);
    } else {
      // Image file, no duration check needed
      const url = URL.createObjectURL(file);
      setAttachedFile(file);
      setPreviewUrl(url);
    }

    event.target.value = '';
  };

  const handleRemoveAttachment = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setAttachedFile(null);
    setPreviewUrl(null);
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setMessageText(newText);

    // Typing indicator logic
    if (onTyping) {
      if (newText.length > 0) {
        onTyping(true);

        // Clear existing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        // Set new timeout to stop typing after 3 seconds of inactivity
        typingTimeoutRef.current = setTimeout(() => {
          onTyping(false);
        }, 3000);
      } else {
        onTyping(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      }
    }
  };

  const handleSend = () => {
    const text = messageText.trim();

    // Can't send empty message without attachment
    if (!text && !attachedFile) return;

    // If there's an attachment, upload it
    if (attachedFile) {
      onUploadMedia(attachedFile, text);
      handleRemoveAttachment();
      setMessageText('');

      // Stop typing indicator
      if (onTyping) {
        onTyping(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      }
      return;
    }

    // Otherwise send text message (with optional reply)
    if (text) {
      onSendMessage(text, replyingTo?.id);
      setMessageText('');

      // Stop typing indicator
      if (onTyping) {
        onTyping(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <footer className="responder-messenger-thread__composer">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime,video/x-msvideo"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      {replyingTo && (
        <div className="responder-messenger-reply-preview">
          <div className="responder-messenger-reply-preview__content">
            <span className="responder-messenger-reply-preview__label">Replying to:</span>
            <span className="responder-messenger-reply-preview__text">
              {replyingTo.content || '(Media)'}
            </span>
          </div>
          <button
            type="button"
            className="responder-messenger-reply-preview__cancel"
            aria-label="Cancel reply"
            onClick={onCancelReply}
          >
            <FaTimes size={10} />
          </button>
        </div>
      )}
      {previewUrl && (
        <div className="responder-messenger-attachment-preview">
          {attachedFile && ACCEPTED_VIDEO_TYPES.includes(attachedFile.type) ? (
            <video src={previewUrl} preload="metadata" />
          ) : (
            <img src={previewUrl} alt={attachedFile?.name || 'Attachment'} />
          )}
          <button
            type="button"
            className="responder-messenger-attachment-remove"
            aria-label="Remove attachment"
            onClick={handleRemoveAttachment}
          >
            <FaTimes size={9} />
          </button>
        </div>
      )}

      <div className="responder-messenger-thread__composer-row">
        <button
          type="button"
          className="responder-messenger-attach-btn"
          aria-label="Attach media"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          <FaPaperclip size={13} />
        </button>

        <input
          value={messageText}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          type="text"
          placeholder="Type a message"
          disabled={disabled}
        />

        <button
          type="button"
          onClick={handleSend}
          aria-label="Send message"
          disabled={disabled || (!messageText.trim() && !attachedFile)}
        >
          <FaPaperPlane size={13} />
        </button>
      </div>
    </footer>
  );
}
